import { Box, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import { useId } from 'react';
import type { PokemonV2 } from '../../pokemon/pokemon';
import type { ItemOption, POKEMON_ITEM_ID } from '../../types/item';
import { getFixedItemIdForPokemonEntity } from '../../lib/pokemon-config';
import { getMegaStoneNameByItemId } from '../../lib/mega-stone';
import { getItemNameById } from '../../utils';

interface PokemonItemSelectProps {
  pokemon: PokemonV2;
  items: ItemOption[];
  value?: POKEMON_ITEM_ID;
  onChange: (itemId: POKEMON_ITEM_ID) => void;
  variant?: 'sidebar' | 'card';
  // カード内でクリック伝播を止めたい場合に利用する
  stopPropagation?: boolean;
}

const resolveItemName = (items: ItemOption[], itemId?: POKEMON_ITEM_ID): string => {
  if (itemId == null) return '';
  return (
    items.find((item) => item.id === itemId)?.name ??
    getItemNameById(itemId) ??
    getMegaStoneNameByItemId(itemId) ??
    ''
  );
};

function PokemonItemSelect({
  pokemon,
  items,
  value,
  onChange,
  variant = 'sidebar',
  stopPropagation,
}: PokemonItemSelectProps) {
  // フォーム固有アイテム（メガストーン/ザシアン等）の固定表示を共通化
  const fixedItemId = getFixedItemIdForPokemonEntity(pokemon);
  const isFixed = fixedItemId != null;
  const displayItemId = isFixed ? fixedItemId : value;
  const displayItemName = resolveItemName(items, displayItemId);
  const isCardVariant = variant === 'card';
  const labelId = useId();

  return (
    <Box sx={isCardVariant ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' } : undefined}>
      {isCardVariant && (
        <Typography variant="caption" sx={{ fontSize: '0.7rem', mb: 0.25 }}>
          道具
        </Typography>
      )}
      <FormControl size="small" sx={isCardVariant ? { width: '16ch' } : { width: '100%', minWidth: 180 }}>
        {!isCardVariant && <InputLabel id={labelId}>道具を選択</InputLabel>}
        <Select
          labelId={isCardVariant ? undefined : labelId}
          label={isCardVariant ? undefined : '道具を選択'}
          value={displayItemId ?? ''}
          disabled={isFixed}
          onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
          onChange={
            isFixed
              ? undefined
              : (event) => {
                  onChange(event.target.value as POKEMON_ITEM_ID);
                }
          }
          MenuProps={
            isCardVariant
              ? undefined
              : {
                  PaperProps: {
                    sx: { maxWidth: 240 },
                  },
                }
          }
          sx={
            isCardVariant
              ? {
                  fontSize: '0.7rem',
                  '& .MuiSelect-select': {
                    backgroundColor: isFixed ? '#f5f5f5' : undefined,
                    color: isFixed ? '#666' : undefined,
                    padding: '2px 6px',
                    textAlign: 'center',
                  },
                }
              : undefined
          }
        >
          {isFixed ? (
            <MenuItem
              value={displayItemId ?? ''}
              sx={isCardVariant ? { fontSize: '0.7rem', justifyContent: 'center' } : undefined}
            >
              {displayItemName}
            </MenuItem>
          ) : (
            items.map((item) => (
              <MenuItem
                key={item.id}
                value={item.id}
                sx={isCardVariant ? { fontSize: '0.7rem', justifyContent: 'center' } : undefined}
              >
                {item.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </Box>
  );
}

export default PokemonItemSelect;
